// Caja y cobros del taller — Taller → Configuración → Caja y cobros (961).
// El taller administra SU caja (punto de emisión, bodega, centro de costo), las
// formas de pago que acepta y sus cajeros sin entrar al menú POS. Por debajo usa
// el motor del POS: una sola secuencia SRI, un solo arqueo, una sola contabilidad.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, CreditCard, Loader2, Power, Save, Users, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { getErrorMessage } from '@/lib/api/client';
import { useTabsStore } from '@/stores/tabs.store';
import { Campo, fieldCls, tallerRpc, QK, fmtFechaHora, money, useDialogoEnLinea } from './shared';

interface Estado {
  activado: boolean; nivel: number; cc_modo: 'partner' | 'contable';
  caja: { id: string; codigo: string; nombre: string; location_id: string | null; bodega: string | null; establecimiento_id: string | null; punto_emision_id: string | null;
          establecimiento: string | null; punto_emision: string | null; centro_costo_id: string | null; cc_contable_id: string | null } | null;
  sesion: { id: string; codigo: string; apertura: string; fondo: number; cajero: string | null } | null;
  cuentas: { caja: boolean; anticipos_clientes: boolean };
  servicios_ok: boolean;
  anticipo_forma: { id: string; nombre: string; cuenta_ok: boolean } | null;
  restringidas: boolean;
  formas: { id: string; codigo: string | null; nombre: string; tipo: string; codigo_sri: string | null; cuenta: string | null; es_anticipo: boolean; en_taller: boolean }[];
  cajeros: { id: string; username: string; perfil: string | null; nivel: number | null; cajero: boolean; tecnico: string | null }[];
  opciones: {
    bodegas: { id: string; nombre: string; codigo: string | null }[];
    puntos: { establecimiento_id: string; establecimiento: string; punto_emision_id: string; punto: string; nombre: string }[];
    centros: { id: string; nombre: string }[];
    centros_contables: { id: string; nombre: string }[];
  };
}

const TIPO: Record<string, string> = { efectivo: 'Efectivo', tarjeta: 'Tarjeta', transferencia: 'Transferencia', cheque: 'Cheque', otro: 'Otro' };

export function CajaCobrosTaller() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const openTab = useTabsStore((s) => s.openTab);
  const { data: e, isLoading, isError, error } = useQuery<Estado>({ queryKey: [QK, 'caja-cobros'], queryFn: () => tallerRpc('caja_cobros_estado') });
  const inv = () => void qc.invalidateQueries({ queryKey: [QK] });

  const [caja, setCaja] = useState({ nombre: '', location_id: '', punto_emision_id: '', centro_costo_id: '', cc_contable_id: '' });
  const [formas, setFormas] = useState<string[]>([]);
  const [cajeros, setCajeros] = useState<string[]>([]);
  useEffect(() => {
    if (!e) return;
    setCaja({ nombre: e.caja?.nombre ?? 'CAJA TALLER', location_id: e.caja?.location_id ?? e.opciones.bodegas[0]?.id ?? '',
      punto_emision_id: e.caja?.punto_emision_id ?? e.opciones.puntos[0]?.punto_emision_id ?? '',
      centro_costo_id: e.caja?.centro_costo_id ?? e.opciones.centros[0]?.id ?? '', cc_contable_id: e.caja?.cc_contable_id ?? e.opciones.centros_contables[0]?.id ?? '' });
    setFormas(e.restringidas ? e.formas.filter((f) => f.en_taller).map((f) => f.id) : e.formas.filter((f) => !f.es_anticipo).map((f) => f.id));
    setCajeros(e.cajeros.filter((c) => c.cajero).map((c) => c.id));
  }, [e]);

  const ccPayload = () => (e?.cc_modo === 'contable' ? { cc_contable_id: caja.cc_contable_id || null } : { centro_costo_id: caja.centro_costo_id || null });
  const activar = useMutation({
    mutationFn: () => tallerRpc<{ hecho: string[] }>('activar', { _d: { location_id: caja.location_id, punto_emision_id: caja.punto_emision_id, ...ccPayload() } }),
    onSuccess: (r) => { toast.success(r.hecho.length ? `Taller activado: ${r.hecho.join(' · ')}` : 'El taller ya estaba activado'); inv(); },
    onError: (x) => toast.error(getErrorMessage(x)),
  });
  const guardarCaja = useMutation({
    mutationFn: () => tallerRpc('caja_configurar', { _d: { nombre: caja.nombre, location_id: caja.location_id || null, punto_emision_id: caja.punto_emision_id || null, ...ccPayload() } }),
    onSuccess: () => { toast.success('Caja actualizada'); inv(); }, onError: (x) => toast.error(getErrorMessage(x)),
  });
  const guardarFormas = useMutation({
    mutationFn: () => tallerRpc('formas_guardar', { _formas: formas }),
    onSuccess: () => { toast.success('Formas de pago del taller guardadas'); inv(); }, onError: (x) => toast.error(getErrorMessage(x)),
  });
  const guardarCajeros = useMutation({
    mutationFn: () => tallerRpc('cajeros_guardar', { _usuarios: cajeros }),
    onSuccess: () => { toast.success('Cajeros guardados'); inv(); }, onError: (x) => toast.error(getErrorMessage(x)),
  });

  if (isLoading) return <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>;
  if (isError || !e) return <div className="p-6 text-sm text-red-600">{getErrorMessage(error)}</div>;
  const admin = e.nivel >= 5;
  const toggle = (arr: string[], set: (v: string[]) => void, id: string) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const selectores = (
    <div className="grid gap-3 sm:grid-cols-2">
      <Campo label="Bodega (repuestos y usados salen de aquí)">
        <select className={fieldCls} disabled={!admin} value={caja.location_id} onChange={(x) => setCaja({ ...caja, location_id: x.target.value })}>
          <option value="">— Elegir —</option>
          {e.opciones.bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}{b.codigo ? ` (${b.codigo})` : ''}</option>)}
        </select>
      </Campo>
      <Campo label="Punto de emisión SRI (serie de las facturas)">
        <select className={fieldCls} disabled={!admin} value={caja.punto_emision_id} onChange={(x) => setCaja({ ...caja, punto_emision_id: x.target.value })}>
          <option value="">— Elegir —</option>
          {e.opciones.puntos.map((p) => <option key={p.punto_emision_id} value={p.punto_emision_id}>{p.nombre}</option>)}
        </select>
      </Campo>
      {e.cc_modo === 'contable' ? (
        <Campo label="Centro de costo contable">
          <select className={fieldCls} disabled={!admin} value={caja.cc_contable_id} onChange={(x) => setCaja({ ...caja, cc_contable_id: x.target.value })}>
            <option value="">— Elegir —</option>
            {e.opciones.centros_contables.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </Campo>
      ) : (
        <Campo label="Centro de costo">
          <select className={fieldCls} disabled={!admin} value={caja.centro_costo_id} onChange={(x) => setCaja({ ...caja, centro_costo_id: x.target.value })}>
            <option value="">— Elegir —</option>
            {e.opciones.centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </Campo>
      )}
    </div>
  );

  return (
    <div className="max-w-4xl space-y-4 p-4 sm:p-6">
      {dlg.ui}
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold"><Wallet className="h-5 w-5" /> Caja y cobros del taller</h1>
        <p className="text-sm text-muted-foreground">La caja del taller, las formas de pago que acepta y quién cobra, sin entrar al POS. Las facturas usan la misma secuencia SRI y el mismo arqueo que el resto del ERP.</p>
      </div>

      {(!e.cuentas.anticipos_clientes || !e.cuentas.caja) && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Falta mapear en Contabilidad {[!e.cuentas.caja && '«Caja»', !e.cuentas.anticipos_clientes && '«Anticipos de clientes»'].filter(Boolean).join(' y ')}: sin esas cuentas no se pueden registrar anticipos.</span>
        </div>
      )}

      {!e.activado ? (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Power className="h-5 w-5 text-primary" /> Activar el taller</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-muted-foreground">Un solo paso: crea la caja del taller con su bodega y punto de emisión, los servicios de diagnóstico y mano de obra, la forma de pago «Anticipo de cliente» (con la cuenta de anticipos) y lo deja a usted como cajero.</p>
            {selectores}
            <div className="flex justify-end">
              <Button disabled={!admin || !caja.location_id || !caja.punto_emision_id} isLoading={activar.isPending} onClick={() => activar.mutate()}><Power className="mr-1 h-4 w-4" /> Activar taller</Button>
            </div>
            {!admin && <p className="text-xs text-muted-foreground">Lo hace un administrador (nivel 5).</p>}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Caja */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Wallet className="h-5 w-5 text-primary" /> Caja del taller · {e.caja?.codigo}</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className={`flex flex-wrap items-center gap-2 rounded-md px-3 py-2 ${e.sesion ? 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200' : 'bg-muted'}`}>
                {e.sesion
                  ? <><CheckCircle2 className="h-4 w-4" /> Turno {e.sesion.codigo} abierto por {e.sesion.cajero ?? '—'} desde {fmtFechaHora(e.sesion.apertura)} · fondo {money(Number(e.sesion.fondo))}</>
                  : <><AlertTriangle className="h-4 w-4 text-amber-600" /> Sin turno abierto: no se puede entregar ni cobrar en efectivo.</>}
                <Button size="sm" variant="ghost" className="ml-auto" onClick={() => openTab({ id: '/taller/caja', title: 'Caja del taller', path: '/taller/caja' })}>
                  {e.sesion ? 'Ver turno' : 'Abrir turno'}
                </Button>
              </div>
              <Campo label="Nombre de la caja"><input className={fieldCls} disabled={!admin} value={caja.nombre} onChange={(x) => setCaja({ ...caja, nombre: x.target.value })} /></Campo>
              {selectores}
              <p className="text-xs text-muted-foreground">Serie actual {e.caja?.establecimiento ?? '—'}-{e.caja?.punto_emision ?? '—'} · bodega {e.caja?.bodega ?? '—'}. Con un turno abierto no se puede cambiar el punto de emisión ni la bodega.</p>
              {admin && <div className="flex justify-end"><Button size="sm" isLoading={guardarCaja.isPending} onClick={() => guardarCaja.mutate()}><Save className="mr-1 h-4 w-4" /> Guardar caja</Button></div>}
            </CardContent>
          </Card>

          {/* Formas de pago */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><CreditCard className="h-5 w-5 text-primary" /> Formas de pago que acepta el taller</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-xs text-muted-foreground">Solo estas aparecen al cobrar anticipos, entregas, planes y usados. El efectivo exige el turno abierto y entra al arqueo. Los pagos por enlace (tarjeta / De Una del portal) se aceptan siempre.</p>
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">N°</th><th className="py-1 pr-2">Usar</th><th className="py-1 pr-2">Forma</th><th className="py-1 pr-2">Tipo</th><th className="py-1 pr-2">Cód. SRI</th><th className="py-1">Cuenta contable</th></tr></thead>
                <tbody>{e.formas.map((f, i) => (
                  <tr key={f.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2 text-muted-foreground">{i + 1}</td>
                    <td className="py-1.5 pr-2">{f.es_anticipo ? <span className="text-xs text-muted-foreground">automática</span>
                      : <input type="checkbox" disabled={e.nivel < 4} checked={formas.includes(f.id)} onChange={() => toggle(formas, setFormas, f.id)} />}</td>
                    <td className="py-1.5 pr-2">{f.nombre}{f.es_anticipo && <span className="ml-1 rounded bg-violet-100 px-1 text-[10px] text-violet-800">anticipo de cliente</span>}</td>
                    <td className="py-1.5 pr-2">{TIPO[f.tipo] ?? f.tipo}</td>
                    <td className="py-1.5 pr-2">{f.codigo_sri ?? '—'}</td>
                    <td className="py-1.5 text-xs">{f.cuenta ?? <span className="text-amber-700">sin cuenta (usa «Caja»)</span>}</td>
                  </tr>))}
                </tbody>
                <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={6} className="py-2 pl-1">{formas.length} forma(s) habilitada(s) de {e.formas.filter((f) => !f.es_anticipo).length}</td></tr></tfoot>
              </table>
              {e.nivel >= 4 && <div className="flex justify-end"><Button size="sm" disabled={!formas.length} isLoading={guardarFormas.isPending} onClick={() => guardarFormas.mutate()}><Save className="mr-1 h-4 w-4" /> Guardar formas</Button></div>}
            </CardContent>
          </Card>

          {/* Cajeros */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Users className="h-5 w-5 text-primary" /> Quién cobra en la caja del taller</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-xs text-muted-foreground">Los marcados pueden abrir el turno, cobrar y entregar en esta caja (y en ninguna otra por este medio). Los administradores (nivel 5) operan cualquier caja.</p>
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">N°</th><th className="py-1 pr-2">Cajero</th><th className="py-1 pr-2">Usuario</th><th className="py-1 pr-2">Perfil</th><th className="py-1">Técnico</th></tr></thead>
                <tbody>{e.cajeros.map((c, i) => (
                  <tr key={c.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2 text-muted-foreground">{i + 1}</td>
                    <td className="py-1.5 pr-2"><input type="checkbox" disabled={!admin} checked={cajeros.includes(c.id)} onChange={() => toggle(cajeros, setCajeros, c.id)} /></td>
                    <td className="py-1.5 pr-2 font-medium">{c.username}</td>
                    <td className="py-1.5 pr-2">{c.perfil ?? '—'}{(c.nivel ?? 0) >= 5 && <span className="ml-1 text-[11px] text-muted-foreground">(opera todas las cajas)</span>}</td>
                    <td className="py-1.5">{c.tecnico ?? '—'}</td>
                  </tr>))}
                </tbody>
                <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={5} className="py-2 pl-1">{cajeros.length} cajero(s)</td></tr></tfoot>
              </table>
              {admin && <div className="flex justify-end"><Button size="sm" isLoading={guardarCajeros.isPending} onClick={() => guardarCajeros.mutate()}><Save className="mr-1 h-4 w-4" /> Guardar cajeros</Button></div>}
            </CardContent>
          </Card>

          {!e.servicios_ok && (
            <div className="flex items-center gap-2 text-sm text-amber-700"><AlertTriangle className="h-4 w-4" /> Faltan los servicios de diagnóstico o mano de obra: pulse «Activar taller» para crearlos.
              <Button size="sm" variant="outline" disabled={!admin} isLoading={activar.isPending} onClick={() => activar.mutate()}>Completar</Button></div>
          )}
        </>
      )}
    </div>
  );
}
